import React, { useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardActions, CardContent, CardMedia, Chip,
    Container, Divider, Grid, Paper, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RateReviewIcon from '@mui/icons-material/RateReview';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import globals from '../globals';
import RedButton from '../components/redButton';
import { buildExamContent } from './examContent';

const BENEFIT_ICONS = {
    result: BarChartIcon,
    review: AssignmentTurnedInIcon,
    feedback: RateReviewIcon,
};

export const mutedText = 'rgba(0,0,0,0.6)';
const sectionTitleSx = { fontSize: { xs: 28, md: 34 }, fontWeight: 400, lineHeight: 1.235 };
const cardTitleSx = { fontSize: 20, fontWeight: 500, lineHeight: 1.6 };
const hoverLiftSx = {
    transition: 'box-shadow 250ms, transform 250ms',
    '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
};
const pinkChipSx = { backgroundColor: globals.colors.lightPink, color: globals.colors.darkBlue, fontWeight: 500 };

export const formatPrice = (price) => `${Number(price).toLocaleString('bg-BG')} €`;

const Section = ({ id, grey, children, maxWidth = 'lg', sx }) => (
    <Box
        component="section"
        id={id}
        sx={{ backgroundColor: grey ? globals.colors.backgroundColor : 'transparent', py: { xs: 4, md: 8 }, scrollMarginTop: 80, ...sx }}
    >
        <Container maxWidth={maxWidth}>{children}</Container>
    </Box>
);

export const CheckList = ({ items, iconSize = 24 }) => (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {items.map((item) => (
            <Box component="li" key={item} sx={{ display: 'flex', gap: 2, alignItems: 'center', minHeight: 48, py: 0.5 }}>
                <CheckIcon sx={{ color: globals.colors.lightRed, fontSize: iconSize }} />
                <Typography sx={{ fontSize: iconSize > 20 ? 16 : 14 }}>{item}</Typography>
            </Box>
        ))}
    </Box>
);

const ModuleCard = ({ index, module }) => (
    <Paper elevation={1} sx={{ p: 3, height: '100%', ...hoverLiftSx }}>
        <Chip label={`Модул ${index + 1}`} sx={{ backgroundColor: globals.colors.darkBlue, color: 'white' }} />
        {module.title && <Typography component="h3" sx={{ ...cardTitleSx, mt: 2 }}>{module.title}</Typography>}
        {module.topics.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1.5, p: 0, listStyle: 'none', display: 'grid', gap: 1 }}>
                {module.topics.map((topic) => (
                    <Box component="li" key={topic.label} sx={{ display: 'flex', gap: 1.25, alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: globals.colors.darkBlue, flexShrink: 0 }}>{topic.label}</Typography>
                        <Typography sx={{ fontSize: 15 }}>{topic.text}</Typography>
                    </Box>
                ))}
            </Box>
        )}
        {(module.durationMinutes || module.handwritten) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {module.durationMinutes && (
                    <Chip size="small" icon={<AccessTimeIcon sx={{ '&&': { color: globals.colors.darkBlue } }} />} label={`${module.durationMinutes} мин`} sx={pinkChipSx} />
                )}
                {module.handwritten && <Chip size="small" label="Задачи с отворен отговор" sx={pinkChipSx} />}
            </Box>
        )}
        {module.description && (
            <>
                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: mutedText }}>{module.description}</Typography>
            </>
        )}
    </Paper>
);

const ProcessSteps = ({ steps }) => (
    <Box component="ol" sx={{ m: 0, mt: 5, p: 0, listStyle: 'none' }}>
        {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            return (
                <Box component="li" key={step.title} sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', columnGap: 2.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                            aria-hidden="true"
                            sx={{
                                display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: '50%', fontWeight: 500, flexShrink: 0,
                                ...(step.optional
                                    ? { backgroundColor: 'white', border: '2px solid rgba(0,0,0,0.24)', color: mutedText }
                                    : { backgroundColor: step.highlighted ? globals.colors.lightRed : globals.colors.darkBlue, color: 'white' }),
                            }}
                        >
                            {i + 1}
                        </Box>
                        {!isLast && <Box sx={{ width: '1px', flex: 1, backgroundColor: 'rgba(0,0,0,0.12)', mt: 1 }} />}
                    </Box>
                    <Box sx={{ pt: 1, pb: isLast ? 0 : 5 }}>
                        <Typography component="h3" sx={{ fontSize: 19, fontWeight: 'bold', lineHeight: 1.4 }}>{step.title}</Typography>
                        {step.text && <Typography sx={{ mt: 0.5, fontSize: 15, color: mutedText }}>{step.text}</Typography>}
                    </Box>
                </Box>
            );
        })}
    </Box>
);

const TeacherCard = ({ teacher }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...hoverLiftSx }}>
        <CardMedia
            component="img"
            image={teacher.image || '/noone.webp'}
            alt={teacher.name}
            sx={{ aspectRatio: '4 / 3', objectFit: 'cover', objectPosition: '50% 20%' }}
        />
        <CardContent sx={{ flex: 1 }}>
            <Typography component="h3" sx={cardTitleSx}>{teacher.name}</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 14, color: mutedText }}>{teacher.role}</Typography>
            {teacher.bio.map((paragraph, i) => (
                <Typography key={i} sx={{ mt: i === 0 ? 1.5 : 1, fontSize: 14, lineHeight: 1.5 }}>{paragraph}</Typography>
            ))}
            {teacher.bioHtml && (
                <Typography component="div" sx={{ mt: 1.5, fontSize: 14, lineHeight: 1.5, '& p': { m: 0, mb: 1 } }}
                    dangerouslySetInnerHTML={{ __html: teacher.bioHtml }} />
            )}
        </CardContent>
        <Divider />
        <CardActions>
            <Button href={teacher.courseUrl || '/courses'} endIcon={<ChevronRightIcon />} sx={{ color: globals.colors.darkBlue, fontWeight: 500 }}>
                Към курса за консултация
            </Button>
        </CardActions>
    </Card>
);

// The exam landing page itself. Used by the public page (/exams/:id) and by the
// admin preview, so both always show the same design.
const ExamLanding = ({ exam, onBuy, showRegistrationNote = false, hideSticky = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expandedFaq, setExpandedFaq] = useState(0);
    const content = useMemo(() => buildExamContent(exam), [exam]);

    const price = formatPrice(exam.price);
    const isPurchased = exam.is_purchased;
    const isAvailable = !exam.is_sold_out;
    const showSticky = isMobile && isAvailable && !isPurchased && !hideSticky;

    const renderBuyButton = () => (
        <RedButton
            onClick={isPurchased ? undefined : onBuy}
            disabled={isPurchased}
            text={isPurchased ? 'Вече закупен' : 'Купи пробна матура'}
            sx={{ fontWeight: 500, textTransform: 'uppercase', minHeight: 48, boxShadow: 6 }}
        />
    );

    const renderPriceLabel = () => (
        <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Цена: <Box component="strong" sx={{ color: 'white', fontWeight: 500 }}>{price}</Box>
        </Typography>
    );

    return (
        <Box sx={{ pb: showSticky ? 9 : 0, overflowX: 'clip' }}>
            {/* Hero */}
            <Box
                component="section"
                id="hero"
                sx={{
                    position: 'relative',
                    color: 'white',
                    backgroundColor: globals.colors.darkBlue,
                    backgroundImage: content.heroImage ? `url("${content.heroImage}")` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: '70% center',
                    py: { xs: 7, md: 16 },
                }}
            >
                <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(90deg, rgba(42,49,83,0.95) 0%, rgba(42,49,83,0.9) 45%, rgba(42,49,83,0.52) 71%, rgba(42,49,83,0.26) 100%)',
                }} />
                <Container maxWidth="lg" sx={{ position: 'relative' }}>
                    <Box sx={{ maxWidth: 700 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.08333em', textTransform: 'uppercase', color: '#f0b3b0' }}>
                            {content.eyebrow}
                        </Typography>
                        <Typography component="h1" sx={{ mt: 2, fontSize: { xs: 32, sm: 44, md: 56 }, lineHeight: 1.167, fontWeight: 400 }}>
                            {exam.title}
                        </Typography>
                        <Typography sx={{ mt: 3, maxWidth: '56ch', fontSize: { xs: 17, md: 20 }, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)' }}>
                            {content.lead}
                        </Typography>
                        {isAvailable ? (
                            <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                                {renderBuyButton()}
                                {renderPriceLabel()}
                            </Box>
                        ) : (
                            <Typography role="status" sx={{ mt: 5, maxWidth: '52ch', backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 1, px: 2, py: 1.5, fontSize: 14 }}>
                                {content.soldOutText}
                            </Typography>
                        )}
                        {isAvailable && showRegistrationNote && (
                            <Typography sx={{ mt: 2, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                                * Нужна е регистрация в сайта за закупуване на пробна матура
                            </Typography>
                        )}
                    </Box>
                </Container>
            </Box>

            {/* What is it */}
            <Section id="what-is">
                <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
                    <Grid item xs={12} md={content.highlights.length > 0 ? 6 : 12}>
                        <Typography component="h2" sx={sectionTitleSx}>{content.whatIsTitle}</Typography>
                        <Typography sx={{ mt: 2, color: mutedText, maxWidth: '50ch' }}>{content.whatIsText}</Typography>
                    </Grid>
                    {content.highlights.length > 0 && (
                        <Grid item xs={12} md={6}>
                            <CheckList items={content.highlights} />
                        </Grid>
                    )}
                </Grid>
            </Section>

            {/* Modules */}
            {content.modules.length > 0 && (
                <Section id="modules" grey>
                    <Typography component="h2" sx={sectionTitleSx}>{content.modulesTitle}</Typography>
                    {content.modulesSubtitle && (
                        <Typography sx={{ mt: 1.5, fontSize: 20, fontWeight: 500, color: globals.colors.darkBlue }}>{content.modulesSubtitle}</Typography>
                    )}
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        {content.modules.map((module, i) => (
                            <Grid item xs={12} md={12 / Math.min(content.modules.length, 3)} key={i}>
                                <ModuleCard index={i} module={module} />
                            </Grid>
                        ))}
                    </Grid>
                    {content.uploadText && (
                        <Paper elevation={1} sx={{ mt: 3, p: 3, display: 'grid', gap: 1.5 }}>
                            <Typography component="h3" sx={cardTitleSx}>{content.uploadTitle}</Typography>
                            <Typography sx={{ fontSize: 14, color: mutedText, maxWidth: '76ch' }}>{content.uploadText}</Typography>
                            <Box sx={{ backgroundColor: globals.colors.backgroundColor, borderRadius: 1, px: 2, py: 1.75, maxWidth: '76ch' }}>
                                <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: mutedText }}>{content.uploadImportantNote}</Typography>
                            </Box>
                        </Paper>
                    )}
                </Section>
            )}

            {/* Benefits */}
            {content.benefits.length > 0 && (
                <Section id="benefits">
                    <Typography component="h2" sx={sectionTitleSx}>{content.benefitsTitle}</Typography>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        {content.benefits.map((benefit) => {
                            const Icon = BENEFIT_ICONS[benefit.icon];
                            return (
                                <Grid item xs={12} md={4} key={benefit.title}>
                                    <Paper elevation={1} sx={{ p: 3, height: '100%', transition: 'box-shadow 250ms', '&:hover': { boxShadow: 6 } }}>
                                        <Icon sx={{ color: globals.colors.darkBlue }} />
                                        <Typography component="h3" sx={{ ...cardTitleSx, mt: 2 }}>{benefit.title}</Typography>
                                        <Typography sx={{ mt: 1, fontSize: 14, color: mutedText }}>{benefit.text}</Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Section>
            )}

            {/* Process */}
            <Section id="process" grey maxWidth="md">
                <Typography component="h2" sx={sectionTitleSx}>{content.processTitle}</Typography>
                <Typography sx={{ mt: 2, color: mutedText }}>{content.processSubtitle}</Typography>
                <ProcessSteps steps={content.processSteps} />
            </Section>

            {/* Teachers + consultation */}
            {content.teachers.length > 0 && (
                <Section id="teachers" sx={{ pb: 4 }}>
                    <Box sx={{ maxWidth: 620 }}>
                        <Typography component="h2" sx={sectionTitleSx}>{content.teachersTitle}</Typography>
                        {content.teachersSubtitle && <Typography sx={{ mt: 2, color: mutedText }}>{content.teachersSubtitle}</Typography>}
                    </Box>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        {content.teachers.map((teacher) => (
                            <Grid item xs={12} sm={6} key={teacher.name}>
                                <TeacherCard teacher={teacher} />
                            </Grid>
                        ))}
                    </Grid>
                    <Paper
                        elevation={6}
                        sx={{
                            mt: 4, p: { xs: 2.5, md: 3.5 }, display: 'flex', gap: 2, alignItems: 'flex-start',
                            backgroundColor: globals.colors.lightPink, borderLeft: `4px solid ${globals.colors.darkBlue}`,
                            transition: 'box-shadow 250ms, transform 250ms',
                            '&:hover': { boxShadow: 10, transform: 'translateY(-3px)' },
                        }}
                    >
                        <GpsFixedIcon sx={{ color: globals.colors.darkBlue, fontSize: 26 }} />
                        <Box>
                            <Typography component="h3" sx={{ fontSize: 24, fontWeight: 'bold', lineHeight: 1.3, color: globals.colors.darkBlue }}>
                                {content.consultationTitle}
                            </Typography>
                            <Typography sx={{ mt: 1.25 }}>{content.consultationText}</Typography>
                            {content.consultationNote && (
                                <Typography sx={{ mt: 1.5, fontSize: 14, color: mutedText }}>{content.consultationNote}</Typography>
                            )}
                        </Box>
                    </Paper>
                </Section>
            )}

            {/* Raffle */}
            {content.raffle && (
                <Section id="raffle" sx={{ pt: 0 }}>
                    <Paper variant="outlined" sx={{ p: 3, ...hoverLiftSx }}>
                        <Typography component="h2" sx={cardTitleSx}>{content.raffle.title}</Typography>
                        <Typography sx={{ mt: 1.5, color: mutedText }}>{content.raffle.text}</Typography>
                        <Typography sx={{ mt: 2, fontSize: 14, fontStyle: 'italic', color: mutedText, backgroundColor: globals.colors.backgroundColor, borderRadius: 1, px: 2, py: 1.5 }}>
                            {content.raffle.note}
                        </Typography>
                    </Paper>
                </Section>
            )}

            {/* Final CTA */}
            {isAvailable && (
                <Section id="buy" sx={{ pt: 0 }}>
                    <Paper elevation={8} sx={{ backgroundColor: globals.colors.darkBlue, color: 'white', p: { xs: 4, md: 7 }, textAlign: 'center' }}>
                        <Typography component="h2" sx={{ ...sectionTitleSx, fontSize: { xs: 24, md: 34 } }}>{content.finalCtaTitle}</Typography>
                        <Typography sx={{ mt: 2, mx: 'auto', maxWidth: '52ch', color: 'rgba(255,255,255,0.85)' }}>{content.finalCtaText}</Typography>
                        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                            {renderBuyButton()}
                            {renderPriceLabel()}
                        </Box>
                    </Paper>
                </Section>
            )}

            {/* FAQ */}
            {content.faq.length > 0 && (
                <Section id="faq" grey maxWidth="md">
                    <Typography component="h2" sx={sectionTitleSx}>{content.faqTitle}</Typography>
                    <Box sx={{ mt: 4 }}>
                        {content.faq.map((item, i) => (
                            <Accordion
                                key={item.question}
                                disableGutters
                                expanded={expandedFaq === i}
                                onChange={() => setExpandedFaq(expandedFaq === i ? -1 : i)}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>{item.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography sx={{ color: mutedText }}>{item.answer}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Section>
            )}

            {/* Sticky mobile CTA */}
            {showSticky && (
                <Paper
                    square
                    elevation={8}
                    sx={{
                        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1200,
                        px: 2, pt: 1, pb: 'calc(8px + env(safe-area-inset-bottom))',
                        display: 'flex', alignItems: 'center', gap: 2,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, letterSpacing: '0.08333em', textTransform: 'uppercase', color: mutedText }}>Цена</Typography>
                        <Typography sx={{ fontWeight: 500 }} noWrap>{price}</Typography>
                    </Box>
                    <RedButton onClick={onBuy} size="medium" text="Купи" sx={{ fontWeight: 500, textTransform: 'uppercase', minHeight: 44, px: 3 }} />
                </Paper>
            )}
        </Box>
    );
};

export default ExamLanding;
